import React from 'react';
import Button from '../../../components/shared/Button';
import FlexParent from '../../../components/shared/flex/FlexParent';
import FlexItem from '../../../components/shared/flex/FlexItem';
import styled from 'styled-components';
import FormGroup from '../../../components/shared/form/FormGroup';
import Label from '../../../components/shared/form/Label';
import Input from '../../../components/shared/form/Input';
import Loader from '../../../components/shared/Loader';

const Container = styled.div`
  margin-top: 100px;
`;

const SSOContainer = styled.div``;

const SSOButton = styled.div`
  border: 1px solid ${props => props.theme.mainColor};
  padding: 5px 10px;
  background-color: white;
  cursor: pointer;
  border-radius: 8px;
`;

const SSOLabel = styled.div`
  font-weight: 500;
`;

const SSOIcon = styled.img`
  width: 25px;
  margin-right: 10px;
`;

const SSOTenantButtons = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const SSO_PROVIDER_ICONS = {
  google: 'google',
  microsoft: 'microsoft',
  'saml-2': 'saml',
  openidconnect: 'openidconnect'
};

const Hr = styled.hr`
  background-color: ${props => props.theme.dustGrey};
  margin: 30px 0;
  border: 0;
  height: 1px;
`;

// Mirrors the throttle window of the API, which rejects a second reset link
// request made within a minute.
const RESET_PASSWORD_COOLDOWN_SECONDS = 60;

class UserSignin extends React.Component {
  state = {
    isLoading: false,
    email: '',
    password: '',
    userEmailIsValid: false,
    isSendingResetLink: false,
    resetPasswordCooldown: 0
  };

  componentWillUnmount() {
    this.clearResetPasswordTimer();
  }

  onChangeInputEmail = event => {
    this.setState({ email: event.target.value });
  };

  onChangeInputPassword = event => {
    this.setState({ password: event.target.value });
  };

  onClickEmailInput = () => {
    this.setState({
      userEmailIsValid: false
    });
  };

  onSubmitFormEmail = event => {
    event.preventDefault();
    const email = this.state.email;
    this.setState({
      isLoading: true
    });
    this.props.testUserEmail({
      email,
      done: () => {
        this.setState({
          userEmailIsValid: true
        });
      },
      fail: () => {
        this.setState({
          userEmailIsValid: false
        });
      },
      always: () => {
        this.setState({
          isLoading: false
        });
      }
    });
  };

  onSubmitFormPwd = event => {
    event.preventDefault();
    const email = this.state.email;
    const password = this.state.password;
    this.setState({
      isLoading: true
    });

    this.props.testUserPwd({
      email,
      password,
      username: email,
      fail: () => {
        this.setState({
          isLoading: false
        });
      }
    });
    this.setState({ password: '' });
  };

  onClickRegister = event => {
    this.props.navigateToRegister();
  };

  clearResetPasswordTimer = () => {
    if (this.resetPasswordTimer) {
      this.resetPasswordTimer = clearInterval(this.resetPasswordTimer);
    }
  };

  startResetPasswordCooldown = () => {
    this.clearResetPasswordTimer();

    this.setState({ resetPasswordCooldown: RESET_PASSWORD_COOLDOWN_SECONDS });

    this.resetPasswordTimer = setInterval(this.countDownResetPassword, 1000);
  };

  countDownResetPassword = () => {
    const resetPasswordCooldown = this.state.resetPasswordCooldown - 1;

    this.setState({ resetPasswordCooldown });

    if (resetPasswordCooldown <= 0) {
      this.clearResetPasswordTimer();
    }
  };

  isResetPasswordDisabled = () => {
    return !this.state.email || this.state.isSendingResetLink || this.state.resetPasswordCooldown > 0;
  };

  onClickResetPassword = event => {
    // The button is already disabled in these cases, but a click fired before
    // the re-render would otherwise send a second request — and a new link
    // invalidates the one already sitting in the attendee's inbox.
    if (this.isResetPasswordDisabled()) {
      return;
    }

    const email = this.state.email;

    this.setState({ isSendingResetLink: true });

    this.props.resetPassword({
      email,
      // The cooldown starts on success only, so a failed request can be
      // corrected and retried right away.
      done: this.startResetPasswordCooldown,
      always: () => {
        this.setState({ isSendingResetLink: false });
      }
    });
  };

  /**
   * Tenants offered by the app, in the order chosen by the organizer. Empty with an API that
   * predates multi-SSO: the legacy `auth_settings` is used instead.
   */
  ssoTenants = () => {
    const { app } = this.props;

    return app && Array.isArray(app.sso_tenants) ? app.sso_tenants : [];
  };

  hasSso = () => {
    const { app } = this.props;

    return this.ssoTenants().length > 0 || !!(app && app.auth_settings);
  };

  // Same rule as before multi-SSO: the API keeps `auth_settings` filled for the app.
  isSsoRequired = () => {
    const { app } = this.props;

    return !!(app && app.auth_settings && app.auth_settings.is_required);
  };

  providerIconUrl = provider =>
    `${process.env.PUBLIC_URL}/images/${SSO_PROVIDER_ICONS[provider] || 'openidconnect'}.svg`;

  renderSsoButtons = () => {
    const { t, app, singleSignOn } = this.props;
    const tenants = this.ssoTenants();

    if (tenants.length > 1) {
      return (
        <SSOTenantButtons>
          {tenants.map(tenant => (
            <SSOButton key={tenant.id} onClick={event => singleSignOn(event, tenant.id)}>
              <FlexParent alignItems={'center'} justifyContent={'center'}>
                <SSOIcon src={tenant.logo_url || this.providerIconUrl(tenant.provider)} alt="" />
                <SSOLabel>{tenant.name}</SSOLabel>
              </FlexParent>
            </SSOButton>
          ))}
        </SSOTenantButtons>
      );
    }

    // A single tenant keeps today's button.
    const tenant = tenants[0];
    const provider = tenant ? tenant.provider : app.auth_settings.provider;

    return (
      <FlexParent alignItems={'center'} justifyContent={'center'}>
        <SSOButton onClick={event => singleSignOn(event, tenant && tenant.id)}>
          <FlexParent alignItems={'center'} justifyContent={'center'}>
            <SSOIcon src={this.providerIconUrl(provider)} />
            <SSOLabel>{t('sso_heading')}</SSOLabel>
          </FlexParent>
        </SSOButton>
      </FlexParent>
    );
  };

  render() {
    const { t, event } = this.props;

    return (
      <Container>
        {this.hasSso() ? (
          <SSOContainer>
            {this.renderSsoButtons()}
            <Hr />
          </SSOContainer>
        ) : null}

        {!this.isSsoRequired() ? (
          <React.Fragment>
            {!this.state.userEmailIsValid ? (
              <form onSubmit={this.onSubmitFormEmail}>
                <FormGroup>
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={'name@domain.com'}
                    value={this.state.email}
                    onChange={this.onChangeInputEmail}
                    autoFocus={'autofocus'}
                  />
                </FormGroup>
                {this.state.isLoading && <Loader />}
                <FlexParent alignItems={'center'}>
                  <FlexItem grow={1}>
                    {event &&
                    ['public', 'protected'].indexOf(event.visibility) !== -1 &&
                    event.status !== 'draft' ? (
                      <Button
                        type={'button'}
                        margin={'0'}
                        padding={'0'}
                        onClick={this.onClickRegister}
                      >
                        {t('btn_create_account')}
                      </Button>
                    ) : null}
                  </FlexItem>
                  <FlexItem>
                    <Button
                      type={'submit'}
                      btntype={'primary'}
                      margin={'3px 0'}
                      disabled={this.state.email ? false : 'disabled'}
                    >
                      {t('btn_next')}
                    </Button>
                  </FlexItem>
                </FlexParent>
              </form>
            ) : (
              <form onSubmit={this.onSubmitFormPwd}>
                <FormGroup>
                  <Label htmlFor="email2">E-mail</Label>
                  <Input
                    id="email2"
                    type="text"
                    value={this.state.email}
                    onClick={this.onClickEmailInput}
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="password">{t('user_password')}</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder={t('user_password')}
                    value={this.state.password}
                    onChange={this.onChangeInputPassword}
                  />
                </FormGroup>

                {this.state.isLoading && <Loader />}

                <FlexParent alignItems={'center'}>
                  <FlexItem grow={1}>
                    <Button
                      type={'button'}
                      margin={'0'}
                      padding={'0'}
                      onClick={this.onClickResetPassword}
                      disabled={this.isResetPasswordDisabled() ? 'disabled' : false}
                    >
                      {this.state.resetPasswordCooldown > 0
                        ? t('btn_forgot_password_retry_in', {
                            seconds: this.state.resetPasswordCooldown
                          })
                        : t('btn_forgot_password')}
                    </Button>
                  </FlexItem>

                  <FlexItem>
                    <Button
                      type={'submit'}
                      btntype={'primary'}
                      margin={'3px 0'}
                      disabled={this.state.password ? false : 'disabled'}
                    >
                      {t('btn_next')}
                    </Button>
                  </FlexItem>
                </FlexParent>
              </form>
            )}
          </React.Fragment>
        ) : null}
      </Container>
    );
  }
}

export default UserSignin;
